import {
  deletePushSubscription,
  savePushSubscription,
  type PushSubscriptionRequest,
} from "@/api/client";
import { useDemoWorkspaceStore } from "@/stores/demoWorkspace";

export type WebPushErrorCode =
  | "unsupported"
  | "home_screen_required"
  | "permission_denied"
  | "permission_dismissed"
  | "service_worker_failed"
  | "invalid_public_key"
  | "invalid_subscription";

export class WebPushError extends Error {
  constructor(
    public readonly code: WebPushErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "WebPushError";
  }
}

export interface WebPushAvailability {
  supported: boolean;
  isIos: boolean;
  isStandalone: boolean;
  requiresHomeScreen: boolean;
  permission: NotificationPermission | "unsupported";
}

export interface WebPushSubscribeResult {
  subscription: PushSubscription;
  created: boolean;
}

export interface WebPushUnsubscribeResult {
  removed: boolean;
  endpoint: string | null;
}

let pushOperationQueue: Promise<unknown> = Promise.resolve();

function queuePushOperation<T>(operation: () => Promise<T>): Promise<T> {
  const result = pushOperationQueue.then(operation, operation);
  pushOperationQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

export function isIosDevice(
  userAgent: string,
  platform = "",
  maxTouchPoints = 0,
): boolean {
  return /iPad|iPhone|iPod/i.test(userAgent)
    || (platform === "MacIntel" && maxTouchPoints > 1);
}

function isStandaloneMode(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone;
  return iosStandalone === true || window.matchMedia("(display-mode: standalone)").matches;
}

export function getWebPushAvailability(): WebPushAvailability {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      supported: false,
      isIos: false,
      isStandalone: false,
      requiresHomeScreen: false,
      permission: "unsupported",
    };
  }

  const isIos = isIosDevice(
    navigator.userAgent,
    navigator.platform,
    navigator.maxTouchPoints,
  );
  const isStandalone = isStandaloneMode();
  const requiresHomeScreen = isIos && !isStandalone;
  const hasNotificationApi = "Notification" in window;
  const supported = !requiresHomeScreen
    && window.isSecureContext
    && "serviceWorker" in navigator
    && "PushManager" in window
    && hasNotificationApi;

  return {
    supported,
    isIos,
    isStandalone,
    requiresHomeScreen,
    permission: hasNotificationApi ? Notification.permission : "unsupported",
  };
}

/** Convert a URL-safe VAPID public key into the BufferSource PushManager expects. */
export function urlBase64ToUint8Array(value: string): Uint8Array {
  const compact = value.trim().replace(/\s+/g, "");
  if (!compact) {
    throw new WebPushError("invalid_public_key", "Push public key is missing.");
  }

  const padding = "=".repeat((4 - (compact.length % 4)) % 4);
  const base64 = (compact + padding).replace(/-/g, "+").replace(/_/g, "/");

  try {
    const decoded = atob(base64);
    return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  } catch {
    throw new WebPushError("invalid_public_key", "Push public key is invalid.");
  }
}

function arrayBufferToBase64Url(value: ArrayBuffer | null): string | null {
  if (!value) return null;
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function serializePushSubscription(
  subscription: PushSubscription,
  userAgent: string | null = typeof navigator === "undefined" ? null : navigator.userAgent,
): PushSubscriptionRequest {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh ?? arrayBufferToBase64Url(subscription.getKey("p256dh"));
  const auth = json.keys?.auth ?? arrayBufferToBase64Url(subscription.getKey("auth"));

  if (!subscription.endpoint || !p256dh || !auth) {
    throw new WebPushError(
      "invalid_subscription",
      "The browser returned an incomplete push subscription.",
    );
  }

  return {
    endpoint: subscription.endpoint,
    expiration_time:
      typeof subscription.expirationTime === "number" ? subscription.expirationTime : null,
    keys: { p256dh, auth },
    user_agent: userAgent ? userAgent.slice(0, 512) : null,
  };
}

function assertPushSupport(): WebPushAvailability {
  const availability = getWebPushAvailability();
  if (availability.requiresHomeScreen) {
    throw new WebPushError(
      "home_screen_required",
      "On iPhone and iPad, open TRACE from its Home Screen icon to enable push notifications.",
    );
  }
  if (!availability.supported) {
    throw new WebPushError(
      "unsupported",
      "Push notifications are not supported in this browser.",
    );
  }
  return availability;
}

function assertPermissionGranted(permission: NotificationPermission): "granted" {
  if (permission === "denied") {
    throw new WebPushError(
      "permission_denied",
      "Notification permission is blocked in your device or browser settings.",
    );
  }
  if (permission !== "granted") {
    throw new WebPushError(
      "permission_dismissed",
      "Notification permission was not granted.",
    );
  }
  return permission;
}

/** Call this directly from a click handler so Safari preserves user activation. */
export async function requestWebPushPermission(): Promise<"granted"> {
  assertLivePushAction();
  const availability = assertPushSupport();
  const permission = availability.permission === "default"
    ? await Notification.requestPermission()
    : availability.permission;
  return assertPermissionGranted(permission as NotificationPermission);
}

async function currentServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration("/")) ?? null;
}

async function ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await currentServiceWorkerRegistration();
  if (existing?.active) return existing;

  try {
    if (!existing) await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    return await new Promise<ServiceWorkerRegistration>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new WebPushError("service_worker_failed", "Service worker activation timed out."));
      }, 10_000);
      navigator.serviceWorker.ready.then(
        (registration) => {
          window.clearTimeout(timeout);
          resolve(registration);
        },
        (error) => {
          window.clearTimeout(timeout);
          reject(error);
        },
      );
    });
  } catch (error) {
    if (error instanceof WebPushError) throw error;
    throw new WebPushError(
      "service_worker_failed",
      "TRACE could not prepare notifications on this device.",
    );
  }
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (useDemoWorkspaceStore.getState().active) return null;
  const availability = getWebPushAvailability();
  if (!availability.supported) return null;
  const registration = await currentServiceWorkerRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
}

export async function isWebPushSubscribed(): Promise<boolean> {
  return (await getCurrentPushSubscription()) !== null;
}

async function subscribe(publicKey: string): Promise<WebPushSubscribeResult> {
  assertLivePushAction();
  const availability = assertPushSupport();
  let permission = availability.permission;
  // Ask before the first unrelated await so Safari/iOS still considers this
  // call part of the user's button gesture.
  if (permission === "default") permission = await Notification.requestPermission();
  assertPermissionGranted(permission as NotificationPermission);

  const registration = await ensureServiceWorkerRegistration();
  let subscription = await registration.pushManager.getSubscription();
  let created = false;

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    created = true;
  }

  try {
    await savePushSubscription(serializePushSubscription(subscription));
  } catch (error) {
    // Do not leave a browser-only subscription behind when its first server
    // registration fails. Existing subscriptions remain intact and can retry.
    if (created) await subscription.unsubscribe().catch(() => false);
    throw error;
  }

  return { subscription, created };
}

export function subscribeToWebPush(publicKey: string): Promise<WebPushSubscribeResult> {
  return queuePushOperation(() => subscribe(publicKey));
}

async function unsubscribe(): Promise<WebPushUnsubscribeResult> {
  assertLivePushAction();
  const subscription = await getCurrentPushSubscription();
  if (!subscription) return { removed: false, endpoint: null };

  const endpoint = subscription.endpoint;
  // Delete server-side first: if the network fails, retaining the local
  // subscription lets the user retry instead of leaving an unreachable row.
  await deletePushSubscription({ endpoint });
  const removed = await subscription.unsubscribe();
  return { removed, endpoint };
}

export function unsubscribeFromWebPush(): Promise<WebPushUnsubscribeResult> {
  return queuePushOperation(unsubscribe);
}

function assertLivePushAction() {
  if (useDemoWorkspaceStore.getState().active) throw new WebPushError("unsupported", "Push is a preview in the demo workspace. Exit the demo to change notifications on this device.");
}
